import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MedicationIcon from "@mui/icons-material/Medication";
import InventoryIcon from "@mui/icons-material/Inventory";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import sfdaMedicine from "../../gql/sfdaMedicine";
import AppButton from "../../shared/AppButton";
import AppLoader from "../../components/AppLoader";
import ConformationModal from "../../components/ConformationModal";

function SfdaMedicineDetail() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const { data, loading, error } = useQuery(sfdaMedicine.GET_SFDA_MEDICINE, {
    variables: { id },
    fetchPolicy: "network-only",
  });

  const [deleteMedicine, { loading: deleting }] = useMutation(
    sfdaMedicine.DELETE_SFDA_MEDICINE,
    {
      onCompleted: () => {
        toast.success(t("Successfully Deleted!"));
        navigate("/sfda-medicine");
      },
      onError: (err) => {
        toast.error(err.message);
      },
    }
  );

  const handleDelete = () => {
    deleteMedicine({ variables: { id } });
  };

  if (loading) return <AppLoader />;

  if (error) {
    toast.error(error.message);
    navigate("/sfda-medicine");
    return null;
  }

  const item = data?.getSfdaMedicine;

  if (!item) {
    toast.error(t("Medicine not found"));
    navigate("/sfda-medicine");
    return null;
  }

  const formatVal = (val) =>
    val === null || val === undefined || val === "" ? "—" : String(val);

  return (
    <div className="superGit_container !p-0">
      {/* Top Action Header */}
      <div className="flex justify-between items-center gap-2 mb-1.5">
        <div className="flex space-x-2 items-center">
          <div
            className="back flex items-center justify-center cursor-pointer transition-all duration-200 hover:opacity-80 active:scale-95"
            onClick={() => navigate("/sfda-medicine")}
            style={{
              backgroundColor: "#0070C2",
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {i18n.language === "en" ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                  fill="white"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
              >
                <g transform="scale(-1,1) translate(-24,0)">
                  <path
                    d="M9 19L10.41 17.59L5.83 13H22V11H5.83L10.42 6.41L9 5L2 12L9 19Z"
                    fill="white"
                  />
                </g>
              </svg>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-black/70 text-lg">
              {t("Medicine Detail")}
            </span>
            {item.supergitMasterCode && (
              <span className="bg-[#0070C2]/10 text-[#0070C2] border border-[#0070C2]/20 text-xs font-bold px-2.5 py-0.5 rounded">
                {item.supergitMasterCode}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-1.5">
          <AppButton
            label={t("Edit")}
            startIcon={<EditIcon />}
            onClick={() => navigate(`/sfda-medicine/form/${item.id}`)}
          />
          <AppButton
            label={t("Delete")}
            bgColor="#dc2626"
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteAlert(true)}
          />
        </div>
      </div>

      {/* Row 1: Product Overview, Dosage & Admin, Packaging & Pricing */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mb-2">
        {/* Card 1: Product Overview */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col">
          <div className="bg-slate-100/80 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MedicalServicesIcon className="!text-[#0070C2] !text-[18px]" />
              <span className="font-bold text-slate-800 text-[13px]">
                {t("Product Information")}
              </span>
            </div>
          </div>
          <div className="p-3 space-y-2">
            <div className="border-b border-slate-100 pb-2">
              <p className="font-bold text-slate-900 text-[14px] leading-tight truncate">
                {formatVal(item.tradeName)}
              </p>
              <p className="text-[12px] font-medium text-slate-500 truncate mt-0.5">
                {formatVal(item.scientificName)}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 bg-slate-50 p-2 rounded border border-slate-200">
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("SGC CODE")}
                </p>
                <p className="text-[12px] font-bold text-[#0070C2]">
                  {formatVal(item.supergitMasterCode)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("REGISTER NO.")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.registerNumber)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("SFDA ID")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.sfdaId)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("REGISTER YEAR")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.registerYear)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("GTIN")}
                </p>
                <p className="text-[12px] font-bold text-slate-800 truncate">
                  {formatVal(item.gtin)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("SCIENTIFIC CODE")}
                </p>
                <p className="text-[12px] font-bold text-slate-800 truncate">
                  {formatVal(item.scientificCode)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Dosage & Administration */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col">
          <div className="bg-slate-100/80 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MedicationIcon className="!text-[#0070C2] !text-[18px]" />
              <span className="font-bold text-slate-800 text-[13px]">
                {t("Dosage & Administration")}
              </span>
            </div>
          </div>
          <div className="p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2 bg-[#0070C2]/5 p-2 rounded border border-[#0070C2]/15">
              <div>
                <p className="text-[10px] uppercase font-bold text-[#0070C2]">
                  {t("STRENGTH")}
                </p>
                <p className="text-[13px] font-bold text-slate-900">
                  {item.strength
                    ? `${item.strength} ${item.strengthUnit || ""}`.trim()
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-[#0070C2]">
                  {t("FORM")}
                </p>
                <p className="text-[13px] font-bold text-slate-900 truncate">
                  {formatVal(item.pharmaceuticalForm)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("ADMIN ROUTE")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.administrationRoute)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("DRUG TYPE")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.drugType)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("SUB TYPE")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.subType)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("DESCRIPTION CODE")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.descriptionCode)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Packaging & Pricing */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col">
          <div className="bg-slate-100/80 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <InventoryIcon className="!text-[#0070C2] !text-[18px]" />
              <span className="font-bold text-slate-800 text-[13px]">
                {t("Packaging & Pricing")}
              </span>
            </div>
          </div>
          <div className="p-3 space-y-2">
            <div className="flex items-center justify-between bg-emerald-50 p-2 rounded border border-emerald-200">
              <div>
                <p className="text-[10px] uppercase font-bold text-emerald-600">
                  {t("PUBLIC PRICE")}
                </p>
                <p className="text-[15px] font-bold text-emerald-800">
                  {item.publicPrice ? `${item.publicPrice} SAR` : "—"}
                </p>
              </div>
              {item.legalStatus && (
                <span className="bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2 py-1 rounded">
                  {item.legalStatus}
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("PACKAGE SIZE")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.packageSize)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("PACKAGE TYPES")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.packageTypes)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("SIZE / UNIT")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {item.size
                    ? `${item.size} ${item.sizeUnit || ""}`.trim()
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-slate-400">
                  {t("PRODUCT CONTROL")}
                </p>
                <p className="text-[12px] font-bold text-slate-800">
                  {formatVal(item.productControl)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Marketing & Manufacturer, Classification & Additional */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2">
        {/* Card 4: Marketing & Manufacturer */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col">
          <div className="bg-slate-100/80 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BusinessIcon className="!text-[#0070C2] !text-[18px]" />
              <span className="font-bold text-slate-800 text-[13px]">
                {t("Marketing & Manufacturer")}
              </span>
            </div>
          </div>
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("MARKETING COMPANY")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.marketingCompany)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("MARKETING STATUS")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.marketingStatus)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("AUTHORIZATION STATUS")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.authorizationStatus)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("MANUFACTURER NAME")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.manufacturerName)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("MANUFACTURER COUNTRY")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.manufacturerCountry)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("AGENT")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.agent)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("ADDITIONAL MANUFACTURER")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.additionalManufacturer)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("ADDL MFR COUNTRY")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.additionalManufacturerCountry)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("COMPANY COUNTRY (EN/AR)")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {item.companyCountryEn || item.companyCountryAr
                  ? `${formatVal(item.companyCountryEn)} / ${formatVal(
                      item.companyCountryAr
                    )}`
                  : "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Card 5: Classification & Additional Details */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col">
          <div className="bg-slate-100/80 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CategoryIcon className="!text-[#0070C2] !text-[18px]" />
              <span className="font-bold text-slate-800 text-[13px]">
                {t("Classification & Additional Details")}
              </span>
            </div>
          </div>
          <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("ATC CODE 1")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.atcCode1)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("ATC CODE 2")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.atcCode2)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("SHELF LIFE")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {item.shelfLife ? `${item.shelfLife} Months` : "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("STORAGE CONDITIONS")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.storageConditions)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("LEGAL STATUS (EN/AR)")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {item.legalStatusEn || item.legalStatusAr
                  ? `${formatVal(item.legalStatusEn)} / ${formatVal(
                      item.legalStatusAr
                    )}`
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("DISTRIBUTION AREA")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.distributionArea)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("COMB. SCIENTIFIC NAME")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.drugCombinationsScientificName)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("COMB. STRENGTH")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {item.drugCombinationsStrengthValue
                  ? `${item.drugCombinationsStrengthValue} ${
                      item.drugCombinationsStrengthUnit || ""
                    }`.trim()
                  : "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase font-bold text-slate-400">
                {t("PRODUCT CONTROL (AR)")}
              </p>
              <p className="text-[12px] font-bold text-slate-800">
                {formatVal(item.productControlAr)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <ConformationModal
        open={showDeleteAlert}
        setOpen={setShowDeleteAlert}
        onDelete={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
}

export default SfdaMedicineDetail;
